/**
 * Canonical UTC calendar range for reporting-service analytics.
 * Always emits `start_date` + `end_date` (YYYY-MM-DD) so the backend
 * `AnalyticsWindow.from_dates` matches timestamptz / created_on filters.
 */

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

export class ReportingAnalyticsDateRange {
  /**
   * @param {{ startDate: string, endDate: string }} range
   */
  constructor(range) {
    this.startDate = ReportingAnalyticsDateRange.normalizeYmd(range?.startDate);
    this.endDate = ReportingAnalyticsDateRange.normalizeYmd(range?.endDate);
    if (this.startDate > this.endDate) {
      throw new Error("Invalid date range: start_date must be on or before end_date");
    }
    const span = ReportingAnalyticsDateRange.inclusiveDayCount(this.startDate, this.endDate);
    if (span > 365) {
      throw new Error("Date range must not exceed 365 days");
    }
    this.inclusiveDays = span;
  }

  /** @param {string | undefined} ymd */
  static normalizeYmd(ymd) {
    const s = String(ymd || "").trim();
    if (!ISO_DATE.test(s)) {
      throw new Error("Dates must be YYYY-MM-DD in UTC");
    }
    return s;
  }

  /** @param {string} start @param {string} end */
  static inclusiveDayCount(start, end) {
    const d0 = new Date(`${start}T00:00:00.000Z`);
    const d1 = new Date(`${end}T00:00:00.000Z`);
    return Math.max(1, Math.round((d1.getTime() - d0.getTime()) / 86400000) + 1);
  }

  /** Query string for endpoints using AnalyticsWindowQueryParams. */
  toWindowQueryString() {
    const sp = new URLSearchParams();
    sp.set("start_date", this.startDate);
    sp.set("end_date", this.endDate);
    return `?${sp.toString()}`;
  }

  /**
   * For legacy endpoints that only accept `days` (rolling window length).
   * Uses the same inclusive span as the calendar picker.
   */
  toDaysQueryString(extra = {}) {
    const sp = new URLSearchParams();
    sp.set("days", String(this.inclusiveDays));
    for (const [k, v] of Object.entries(extra)) {
      if (v != null) sp.set(k, String(v));
    }
    return `?${sp.toString()}`;
  }
}

export const reportingAnalyticsDateRange = ReportingAnalyticsDateRange;
